<template>
  <integration-tab-wrapper heading="Launch Control" logo-path="/images/launch-control-icon.png" :is-onboarding-integration="isOnboardingIntegration">
    <template v-if="!isOnboardingIntegration" v-slot:buttons>
      <button-link
        v-if="webhook && webhook.is_active"
        @click="pauseLaunchControlIntegration"
        class="mr-100"
        :loading="isPauseLoading"
        :disabled="isPauseLoading"
      >Pause Integration
      </button-link>

      <button-link
        v-if="webhook && !webhook.is_active"
        @click="resumeLaunchControlIntegration"
        class="mr-100"
        :loading="isResumeLoading"
        :disabled="isResumeLoading"
      >Resume Integration
      </button-link>

      <button-link
        @click="disconnectLaunchControlIntegration"
        :loading="isIntegrationDeleting"
        :disabled="isIntegrationDeleting"
      >Disconnect Launch Control
      </button-link>
    </template>

    <section-page>
      <app-loading-section :loading="isWebhookLoading || isIntegrationCreating">
        <!--Pause Notification-->
        <div v-if="webhook && !webhook.is_active" class="pause_notification flex flex-vertical-center">
          <icon-default icon="warning" class="mr-100"></icon-default>
          Launch Control integration is paused. No leads will be imported from Launch Control until the integration is
          resumed.
        </div>

        <!--API Key-->
        <div class="mt-200 mb-400 flex flex-between flex-vertical-center">
          <div class="mr-200">
            <h3 class="api_heading">API Key</h3>
            <p class="api_description">Copy this API key into your Launch Control account to have leads come into
              Forefront</p>
          </div>

          <div class="is-flex-shrink-0 flex flex-vertical-center">
            <p>{{ apiKey }}</p>
            <a @click="copyApiKey" class="ml-100">copy</a>
          </div>
        </div>

        <grid-row v-if="webhook">
          <!--Default Settings-->
          <grid-column :size="5">
            <h2 class="column_heading">Lead Defaults</h2>
            <p class="column_description">These settings will be applied to all leads created by this integration</p>

            <form-field-select-status
              label="Default Status"
              v-model="webhook.status_id"
              class="mb-200"
              help-text="Status to place new Leads created by this integration"
            ></form-field-select-status>

            <form-field-select-users
              label="Default User"
              v-model="webhook.user_id"
              class="mb-200"
              help-text="User to assign new Leads created by this integration"
            ></form-field-select-users>

            <form-field-select-temperature
              label="Default Temperature"
              v-model="webhook.temperature"
              class="mb-200"
              help-text="Temperature to set for new Leads created by this integration"
            ></form-field-select-temperature>

            <launch-control-campaign-select
              label="Default Campaign"
              v-model="campaign"
              class="mb-200"
            />

            <button-default
              @click="updateLaunchControlWebhook"
              :loading="isWebhookUpdating"
              :disabled="isWebhookUpdating"
            >Update Default Settings
            </button-default>
          </grid-column>

          <!--Field Maps-->
          <grid-column :size="6" class="is-offset-1">
            <h2 class="column_heading">Field Mapping</h2>
            <p class="column_description">Inbound leads from Launch Control will create a new lead with data mapped this
              way</p>

            <!--Default Fields-->
            <div>
              <h4 class="column_subheading">Default Fields</h4>
              <p class="column_description">List of fields that directly map between Launch Control and Forefront</p>

              <ul class="mb-150" style="max-width: 360px">
                <li class="list_heading">
                  <span>Launch Control</span>
                  <span>Forefront</span>
                </li>
                <li class="list_item">
                  <span>Property Address</span>
                  <span>Lead Address</span>
                </li>
                <li class="list_item">
                  <span>Property City</span>
                  <span>Lead City</span>
                </li>
                <li class="list_item">
                  <span>Property State</span>
                  <span>Lead State</span>
                </li>
                <li class="list_item">
                  <span>Property Zip</span>
                  <span>Lead Postal Code</span>
                </li>
                <li class="list_item">
                  <span>First Name</span>
                  <span>Contact First Name</span>
                </li>
                <li class="list_item">
                  <span>Last Name</span>
                  <span>Contact Last Name</span>
                </li>
                <li class="list_item">
                  <span>Verified Phone Number</span>
                  <span>Contact Phone 1</span>
                </li>
                <li class="list_item">
                  <span>Mailing Address</span>
                  <span>Contact Address</span>
                </li>
                <li class="list_item">
                  <span>Mailing City</span>
                  <span>Contact City</span>
                </li>
                <li class="list_item">
                  <span>Mailing State</span>
                  <span>Contact State</span>
                </li>
                <li class="list_item">
                  <span>Mailing Zip</span>
                  <span>Contact Postal Code</span>
                </li>
              </ul>

              <p class="list_note">All notes from Launch Control will be added to the lead with a prefix of "From Launch
                Control:"</p>

              <p class="list_note">All messages from Launch Control will be turned into comm logs so they will show up
                in
                the text history of the lead</p>
            </div>

            <!--Custom Fields-->
            <div>
              <h4 class="column_subheading">Custom Fields</h4>
              <p class="column_description">These fields don't directly map and you'll need to choose a phone or text
                custom field as appropriate, or create a new custom field.</p>

              <launch-control-settings-custom-fields
                :webhook="webhook"
                @webhookUpdated="updateLocalWebhookData"
              />
            </div>
          </grid-column>
        </grid-row>
      </app-loading-section>
    </section-page>
  </integration-tab-wrapper>
</template>

<script>
import api from "../../../api";
import utils from "../../../services/utils";
import IntegrationBackButton from "../components/IntegrationBackButton.vue";
import LaunchControlSettingsCustomFields from "../components/LaunchControlSettingsCustomFields.vue";
import LaunchControlCampaignSelect from "../components/LaunchControlCampaignSelect.vue";
import IntegrationTabWrapper from "../components/IntegrationTabWrapper.vue";

export default {
  name: "IntegrationLaunchControl",
  components: {
    IntegrationTabWrapper,
    IntegrationBackButton,
    LaunchControlSettingsCustomFields,
    LaunchControlCampaignSelect
  },
  props: {
    isOnboardingIntegration: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      isIntegrationCreating: false,
      isIntegrationDeleting: false,
      isPauseLoading: false,
      isResumeLoading: false,
      isWebhookLoading: false,
      isWebhookUpdating: false,
      webhook: null,
      campaign: {
        id: null,
        channel: null,
        label: "",
      },
    };
  },
  computed: {
    hasLaunchControlIntegration() {
      return this.$store.getters.hasLaunchControlIntegration;
    },
    launchControlIntegration() {
      return this.$store.getters.launchControlIntegration;
    },
    apiKey() {
      return this.webhook ? this.webhook.token : null;
    }
  },
  mounted() {
    if (!this.hasLaunchControlIntegration) {
      this.isIntegrationCreating = true;
      const market = this.$store.getters.market;
      const data = {
        market_id: market.id,
        label: "Launch Control (" + market.label + ")"
      };

      api.launchControl.create(data).then((response) => {
        this.updateLocalWebhookData(response.data)
        this.isIntegrationCreating = false;
      });
    } else {
      this.isWebhookLoading = true;

      api.webhooks.get(this.launchControlIntegration.data.webhook_id).then(response => {
        this.updateLocalWebhookData(response.data);
        this.isWebhookLoading = false;
      });
    }
  },
  methods: {
    disconnectLaunchControlIntegration() {
      this.isIntegrationDeleting = true;

      api.launchControl.delete(this.launchControlIntegration.id).then(() => {
        this.isIntegrationDeleting = false;
        this.$router.push({name: 'settings', params: {tab: 'integrations'}})
      });
    },
    pauseLaunchControlIntegration() {
      const data = this.webhook;
      data.is_active = false;
      this.isPauseLoading = true;

      api.launchControl.update(this.launchControlIntegration.id, data).then((response) => {
        this.updateLocalWebhookData(response.data);
        this.isPauseLoading = false;
      });
    },
    resumeLaunchControlIntegration() {
      const data = this.webhook;
      data.is_active = true;
      this.isResumeLoading = true;

      api.launchControl.update(this.launchControlIntegration.id, data).then((response) => {
        this.updateLocalWebhookData(response.data);
        this.isResumeLoading = false;
      });
    },
    updateLaunchControlWebhook() {
      this.isWebhookUpdating = true;

      const data = this.webhook;

      // Set webhook campaign id if necessary
      if (this.campaign.id && this.campaign.id !== 0) {
        data.campaign_id = this.campaign.id;
      }

      // Set new campaign if
      if (this.campaign.id === 0) {
        data.new_campaign = this.campaign;
      }

      api.launchControl.update(this.launchControlIntegration.id, data).then((response) => {
        this.updateLocalWebhookData(response.data);
        this.isWebhookUpdating = false;

        this.$buefy.toast.open({
          message: "Launch Control was updated",
          type: "is-success"
        });
      });
    },
    updateLocalWebhookData(webhook) {
      this.webhook = webhook;
      this.campaign.id = webhook.campaign_id;
    },
    copyApiKey() {
      utils.copyToClipboard(this.apiKey);
    },
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/variables/variables";

.integration_loading {
  padding: 1.5rem 0;
  text-align: center;
  font-style: italic;
}

.integration_info {
  max-width: 760px;
  margin-right: 2rem;

  &--description {
    margin-bottom: 12px;
    line-height: 1.35;
  }
}

.pause_notification {
  border-radius: 6px;
  padding: 12px 1rem;
  background-color: $secondary;
  color: $white;
  font-weight: bold;
  line-height: 1.3;
}

.api_heading {
  margin-bottom: 4px;
  font-weight: bold;
}

.api_description {
  font-size: 0.875rem;
  color: $text-muted;
}

.column {
  &_heading {
    margin-bottom: 4px;
    font-size: 1.125rem;
    font-weight: bold;
  }

  &_subheading {
    margin-bottom: 4px;
    font-weight: bold;
  }

  &_description {
    margin-bottom: 1.25rem;
    line-height: 1.25;
    font-size: 0.875rem;
    color: $text-muted;
  }
}

.list {
  &_heading {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px solid $border;
    font-size: 0.75rem;
  }

  &_item {
    display: flex;
    justify-content: space-between;
    padding: 6px 0;
    border-bottom: 1px solid $border;
    font-size: 0.875rem;
  }

  &_note {
    margin-bottom: 1.5rem;
    line-height: 1.35;
  }
}
</style>
